import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "material-calendar" }
const _hoisted_2 = { class: "calendar-header" }
const _hoisted_3 = {
  key: 0,
  class: "calendar-grid"
}
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "day-number" }
const _hoisted_6 = {
  key: 0,
  class: "data-dot"
}
const _hoisted_7 = {
  key: 1,
  class: "year-grid"
}
const _hoisted_8 = ["onClick"]

import { ref, computed } from 'vue';

interface CalendarDay {
  date: Date;
  day: number;
  currentMonth: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MaterialCalendar',
  props: {
    modelValue: {},
    activityDates: {}
  },
  emits: ['update:modelValue', 'close', 'month-change'],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const today = new Date();
const initialDate = props.modelValue ? new Date(props.modelValue) : today;
// Validate initialDate
const validDate = isNaN(initialDate.getTime()) ? today : initialDate;

const currentMonth = ref(validDate.getMonth());
const currentYear = ref(validDate.getFullYear());
const viewMode = ref<'month' | 'year'>('month');
const yearPage = ref(Math.floor(currentYear.value / 12) * 12); // Grid of 12 years

const selectedYear = computed(() => {
    return props.modelValue ? new Date(props.modelValue).getFullYear() : currentYear.value;
});

const weekDays = ['Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa', 'Do'];

const currentMonthName = computed(() => {
  return new Date(currentYear.value, currentMonth.value).toLocaleString('it-IT', { month: 'long' });
});

const headerLabel = computed(() => {
    if (viewMode.value === 'month') {
        return `${currentMonthName.value} ${currentYear.value}`;
    } else {
        return `${yearPage.value} - ${yearPage.value + 11}`;
    }
});

const yearRange = computed(() => {
    const years = [];
    for (let i = 0; i < 12; i++) {
        years.push(yearPage.value + i);
    }
    return years;
});

const calendarDays = computed<CalendarDay[]>(() => {
  const days: CalendarDay[] = [];
  const firstDay = new Date(currentYear.value, currentMonth.value, 1);
  const lastDay = new Date(currentYear.value, currentMonth.value + 1, 0);
  
  // Adjust logic for Monday start (0=Sun -> 6, 1=Mon -> 0)
  let startDayOfWeek = firstDay.getDay() - 1; 
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  // Previous month padding
  const prevMonthLastDay = new Date(currentYear.value, currentMonth.value, 0).getDate();
  for (let i = startDayOfWeek - 1; i >= 0; i--) {
     days.push({
       date: new Date(currentYear.value, currentMonth.value - 1, prevMonthLastDay - i),
       day: prevMonthLastDay - i,
       currentMonth: false
     });
  }

  // Current month
  for (let i = 1; i <= lastDay.getDate(); i++) {
    days.push({
      date: new Date(currentYear.value, currentMonth.value, i),
      day: i,
      currentMonth: true
    });
  }

  // Next month padding
  const remainingCells = 42 - days.length; // 6 rows * 7
  for (let i = 1; i <= remainingCells; i++) {
     days.push({
       date: new Date(currentYear.value, currentMonth.value + 1, i),
       day: i,
       currentMonth: false
     });
  }

  return days;
});

function prev() {
  if (viewMode.value === 'month') {
      prevMonth();
  } else {
      yearPage.value -= 12; // Go back 12 years
  }
}

function next() {
  if (viewMode.value === 'month') {
      nextMonth();
  } else {
      yearPage.value += 12; // Go forward 12 years
  }
}

function prevMonth() {
  if (currentMonth.value === 0) {
    currentMonth.value = 11;
    currentYear.value--;
  } else {
    currentMonth.value--;
  }
  emit('month-change', { year: currentYear.value, month: currentMonth.value });
}

function nextMonth() {
  if (currentMonth.value === 11) {
    currentMonth.value = 0;
    currentYear.value++;
  } else {
    currentMonth.value++;
  }
  emit('month-change', { year: currentYear.value, month: currentMonth.value });
}

function toggleViewMode() {
    if (viewMode.value === 'month') {
        viewMode.value = 'year';
        yearPage.value = Math.floor(currentYear.value / 12) * 12; // Center roughly
    } else {
        viewMode.value = 'month';
    }
}

function selectYear(year: number) {
    currentYear.value = year;
    viewMode.value = 'month';
    emit('month-change', { year: currentYear.value, month: currentMonth.value });
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isSelected(date: Date) {
  return props.modelValue === formatDate(date);
}

function isToday(date: Date) {
  return formatDate(date) === formatDate(new Date());
}

function hasData(date: Date) {
  if (!props.activityDates) return false;
  return props.activityDates.includes(formatDate(date));
}

function selectDate(date: Date) {
  emit('update:modelValue', formatDate(date));
  emit('close');
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("button", {
        class: "nav-btn",
        onClick: prev
      }, [...(_cache[0] || (_cache[0] = [
        _createElementVNode("svg", {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            d: "M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z",
            fill: "currentColor"
          })
        ], -1)
      ]))]),
      _createElementVNode("span", {
        class: "month-label clickable",
        onClick: toggleViewMode
      }, _toDisplayString(headerLabel.value), 1),
      _createElementVNode("button", {
        class: "nav-btn",
        onClick: next
      }, [...(_cache[1] || (_cache[1] = [
        _createElementVNode("svg", {
          width: "24",
          height: "24",
          viewBox: "0 0 24 24"
        }, [
          _createElementVNode("path", {
            d: "M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z",
            fill: "currentColor"
          })
        ], -1)
      ]))])
    ]),
    (viewMode.value === 'month')
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(weekDays, (day) => {
            return _createElementVNode("div", {
              key: day,
              class: "weekday-label"
            }, _toDisplayString(day), 1)
          }), 64)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarDays.value, (date, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: _normalizeClass(["day-cell", {
          'current-month': date.currentMonth,
          'selected': isSelected(date.date),
          'has-data': hasData(date.date),
          'today': isToday(date.date)
        }]),
              onClick: ($event: any) => (selectDate(date.date))
            }, [
              _createElementVNode("span", _hoisted_5, _toDisplayString(date.day), 1),
              (hasData(date.date))
                ? (_openBlock(), _createElementBlock("div", _hoisted_6))
                : _createCommentVNode("", true)
            ], 10, _hoisted_4))
          }), 128))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_7, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(yearRange.value, (year) => {
            return (_openBlock(), _createElementBlock("div", {
              key: year,
              class: _normalizeClass(["year-cell", { 'current-year': year === currentYear.value, 'selected': year === selectedYear.value }]),
              onClick: ($event: any) => (selectYear(year))
            }, _toDisplayString(year), 11, _hoisted_8))
          }), 128))
        ]))
  ]))
}
}

})